import Vue from 'vue'
import * as _ from 'lodash'
import SmoothScroll from 'smooth-scroll'
import { validationMixin } from 'vuelidate'
import { required, email, sameAs } from 'vuelidate/lib/validators'
import SecureFields from '~plugins/secure-fields.ts'

import ScrollWagon from '~components/scroll-wagon/scroll-wagon.vue'
import scrollToFirstInvalidField from '~utils/scroll-to-first-invalid-field'
import axios from '~plugins/axios'

const SECURE_3D_FAILURE_CODES = ['E', 'N']

export default Vue.extend({
  components: {
    ScrollWagon,
  },
  mixins: [validationMixin],

  data() {
    return {
      formFields: {
        billingFirstName: '',
        billingLastName: '',
        email: '',
        emailConfirmation: '',
        street: '',
        zipCode: '',
        city: '',
        country: '',
        expiryDate: '',
        terms: false,
        cardholder: {
          name: '',
          email: '',
        },
      },
      secureFields: null,
      secureFieldsTouched: false,
      secureFieldsStatus: {
        cvv: {
          focused: false,
          filled: false,
          valid: false,
        },
        cardNumber: {
          focused: false,
          filled: false,
          valid: false,
        },
      },
      secureFieldsReady: false,
      cardholderLocked: false,
      defaultCardholderName: '',
      defaultCardholderEmail: '',
    }
  },

  validations: {
    formFields: {
      billingFirstName: { required },
      billingLastName: { required },
      email: { required, email },
      emailConfirmation: { required, email, sameAsEmail: sameAs('email') },
      street: { required },
      zipCode: { required },
      city: { required },
      country: { required },
      expiryDate: {
        required,
        validExpiry: (value) => (/^[01]\d\/\d\d$/i).test(value),
      },
      terms: { accepted: (value) => !!value },
    },
  },

  computed: {
    purchase() {
      return this.$store.getters['sales/purchase']
    },
    errors() {
      return this.$store.getters['sales/errors']
    },
    hasErrors() {
      return this.errors.length > 0
    },
    isLoading() {
      return this.$store.getters['sales/loading']
    },
    cardExpirationMonth() {
      return this.formFields.expiryDate.split('/')[0]
    },
    cardExpirationYear() {
      return this.formFields.expiryDate.split('/')[1]
    },
    status3DSecure() {
      return this.$route.query.status_3d
    },
    emailErrorType() {
      if (!this.$v.formFields.email.required) {
        return 'required'
      }
      if (!this.$v.formFields.email.email) {
        return 'email'
      }
      return 'invalid'
    },
    emailConfirmationErrorType() {
      if (!this.$v.formFields.emailConfirmation.required) {
        return 'required'
      }
      if (!this.$v.formFields.emailConfirmation.email) {
        return 'email'
      }
      if (!this.$v.formFields.emailConfirmation.sameAsEmail) {
        return 'sameAsEmail'
      }
      return 'invalid'
    },
    expiryDateErrorType() {
      if (!this.$v.formFields.emailConfirmation.required) {
        return 'required'
      }
      if (!this.$v.formFields.emailConfirmation.expiryDate) {
        return 'expiryDate'
      }
      return 'invalid'
    },

    shouldCvvDisplayError() {
      return this.secureFieldsTouched && !this.secureFieldsStatus.cvv.valid
    },
    shouldCardNumberDisplayError() {
      return this.secureFieldsTouched && !this.secureFieldsStatus.cardNumber.valid
    },
    shouldCvvDisplayLabel() {
      return this.secureFieldsStatus.cvv.focused || this.secureFieldsStatus.cvv.filled
    },
    shouldCardNumberDisplayLabel() {
      return this.secureFieldsStatus.cardNumber.focused || this.secureFieldsStatus.cardNumber.filled
    },
  },

  watch: {
    async isLoading(newValue, oldValue) {
      if (!newValue && oldValue) {
        await Vue.nextTick()

        const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
        const offset = vw >= 768 ? this.brandConfig.header.offset : this.brandConfig.header.offsetMobile

        const scroll = new SmoothScroll()
        scroll.animateScroll(this.$refs.error, undefined, { offset: offset + 15, updateURL: false })
      }
    },
  },

  async created() {
    if (this.status3DSecure) {
      await this.$store.dispatch('sales/loadStateFromSession')
      const formFieldsFromStorage = sessionStorage.getItem('formFields')

      if (formFieldsFromStorage) {
        this.$set(this, 'formFields', JSON.parse(formFieldsFromStorage))
      }

      if (typeof (this.purchase.datatransTransactionId) === 'undefined') {
        // Status is 3d secure, but there is no transaction anymore -> user clicked back on confirmation
        // Redirect to first page to initiate new sale flow
        await this.$router.replace(this.$i18nTo({ name: 'voucherSaleAmount' }))
        return
      }

      if (SECURE_3D_FAILURE_CODES.indexOf(this.status3DSecure) !== -1) {
        await this.$store.dispatch('sales/setErrors', {
          errors: ['secure3d'],
        })

        await this.initializeSale()
      } else {
        await this.confirmPurchase({ redirectRequired: true })
      }
    } else {
      // This was previously in mounted
      if (typeof (this.$store.state.sales.desiredAmountInCents) === 'undefined') {
        await this.$router.replace(this.$i18nTo({ name: 'voucherSaleAmount' }))
        return
      }

      await this.initializeSale()
    }
  },
  methods: {
    updateDefaults() {
      this.defaultCardholderName = this.formFields.cardholder.name || [this.formFields.billingFirstName, this.formFields.billingLastName].filter((v) => !!v).join(' ')
      this.defaultCardholderEmail = this.formFields.cardholder.email || this.formFields.email
    },
    onChangeCardholder(data) {
      this.cardholderLocked = true

      this.formFields.cardholder = data
      this.initializePaymentIntent()
    },
    onUnlock() {
      this.cardholderLocked = false
      this.secureFieldsReady = false
    },
    // Ludwig's new method
    async initializePaymentIntent() {
      this.cardholderLocked = true
      const voucherSaleId = this.purchase.id
      const { data } = await axios.post(`voucher_sales/${voucherSaleId}/initialize_payment_intent`, {
        cardholder: this.formFields.cardholder,
      })
      if (data.status === 'payment_initialized') {
        await this.$store.dispatch('sales/amendPurchase', { data })
        this.resetInsecureFields()
        await this.initializeSecureFields()
        this.subscribeSecureFieldsEvents()
      }
    },
    async initializeSale() {
      await this.$store.dispatch('sales/initiatePurchase', { locale: this.$i18n.locale })
    },
    resetInsecureFields() {
      this.formFields.expiryDate = ''
      this.$v.formFields.expiryDate.$reset()
    },
    async initializeSecureFields() {
      this.secureFieldsReady = false
      this.secureFieldsTouched = false
      this.secureFieldsStatus = {
        cardNumber: {
          focused: false,
          filled: false,
          valid: false,
        },
        cvv: {
          focused: false,
          filled: false,
          valid: false,
        },
      }

      this.secureFields = new SecureFields()
      if (!this.purchase.datatransTransactionId) { return }

      this.secureFields.init(
        this.purchase.datatransTransactionId,
        {
          cardNumber: {
            placeholderElementId: 'card-number',
            placeholder: this.tr('pages.sales.checkout.form.payment.cardNumber.placeholder'),
            inputType: 'tel',
          },
          cvv: {
            placeholderElementId: 'cvv-number',
            placeholder: this.tr('pages.sales.checkout.form.payment.cvv.placeholder'),
            inputType: 'tel',
          },
        },
        {
          styles: this.secureFieldStyles,
          debug: false,
        },
      )
    },
    subscribeSecureFieldsEvents() {
      this.secureFields.on('validate', (event) => {
        if (event.hasErrors) {
          _.forEach(event.fields, (value, fieldName) => {
            this.secureFieldsStatus[fieldName].valid = event.secureFieldsStatus[fieldName].valid
            this.secureFields.setStyle(fieldName, `border-color: ${!event.fields[fieldName].valid ? '#c00' : '#666'}`)
          })

          this.$store.dispatch('sales/setLoadingState', { loading: false })
        }
      })

      this.secureFields.on('ready', () => {
        this.secureFieldsReady = true
      })

      this.secureFields.on('error', async () => {
        await this.initializeSecureFields()
        await this.initializePaymentIntent()
        await this.$store.dispatch('sales/setErrors', { errors: ['declined'] })
        await this.$store.dispatch('sales/setLoadingState', { loading: false })
      })

      this.secureFields.on('change', ({ event, fields }) => {
        if (event.type === 'focus') {
          this.secureFieldsStatus[event.field].focused = true
          this.secureFields.setStyle(event.field, 'border-color: #666')
        } else if (event.type === 'blur') {
          this.secureFieldsStatus[event.field].focused = false
          this.secureFieldsStatus[event.field].valid = fields[event.field].valid
          if (this.secureFieldsTouched) {
            this.secureFields.setStyle(event.field, `border-color: ${!fields[event.field].valid ? '#c00' : '#666'}`)
          }
        }

        this.secureFieldsStatus[event.field].valid = fields[event.field].valid
        this.secureFieldsStatus[event.field].filled = fields[event.field].length > 0
      })

      this.secureFields.on('success', async (data) => {
        if (data.transactionId) {
          if (data.redirect) { // Does the card have secure3d check
            await this.$store.dispatch('sales/secure3dRequired', {
              purchaseId: this.purchase.id,
            })

            if (this.purchase.secure3dRequired) {
              sessionStorage.setItem('formFields', JSON.stringify(this.formFields))
              window.location = data.redirect
              return
            }
          }

          await this.confirmPurchase({ redirectRequired: false })
        }
      })
    },
    touchSecureFields() {
      this.secureFieldsTouched = true

      if (!this.secureFieldsStatus.cvv.valid) {
        this.secureFields.setStyle('cvv', 'border-color: #c00')
      }
      if (!this.secureFieldsStatus.cardNumber.valid) {
        this.secureFields.setStyle('cardNumber', 'border-color: #c00')
      }
    },
    onSubmit() {
      this.$v.$touch()
      this.touchSecureFields()

      if (this.$v.$invalid || !this.secureFieldsStatus.cardNumber.valid || !this.secureFieldsStatus.cvv.valid) {
        scrollToFirstInvalidField()
        return
      }

      this.$store.dispatch('sales/resetErrors')
      this.$store.dispatch('sales/setLoadingState', { loading: true })

      this.secureFields.submit({
        expm: this.cardExpirationMonth,
        expy: this.cardExpirationYear,
      })
    },
    async confirmPurchase({ redirectRequired }) {
      const { cvv: _cvv, terms: _terms, ...data } = this.formFields

      await this.$store.dispatch('sales/checkout', {
        purchaseId: this.purchase.id,
        redirectRequired,
        ...data,
        locale: this.$i18n.locale,
      })

      if (this.hasErrors) {
        await this.initializePaymentIntent()
      } else {
        this.$router.push(
          this.$i18nTo({
            name: 'voucherSaleConfirmation',
          }),
        )
      }
    },
    scrollTo(element) {
      window.scroll({
        behavior: 'smooth',
        left: 0,
        top: element.offsetTop,
      })
    },
  },
})
